import { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Image,
  Input,
  message,
  Row,
  Select,
  Spin,
  Tag,
  Typography,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { AppPageHeader } from "components/AppPageHeader";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import {
  getOrderByTrayCode,
  getDeliveryFailureDispositions,
  markOrderFailed,
  resetTrayOrder,
  resetMarkFailedStatus,
} from "store/ppd/markHoldOrdersSlice";
import { getCities, getDarkStores } from "store/admin-slice/adminServicesSlice";
import { ITrayOrder, normalizeTrayCodes } from "common/interface/IMarkHoldOrder";

const { Text } = Typography;

const MESSAGE_KEY = "mark_hold_orders";

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 860px;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  &:last-child {
    border-bottom: none;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 24px;
  background: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  color: #888;
  font-size: 15px;
`;

const InfoLabel = styled(Text)`
  display: block;
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 2px;
`;

const InfoValue = styled(Text)`
  display: block;
  font-size: 14px;
  font-weight: 500;
`;

const STATUS_COLOR_MAP: Record<string, string> = {
  DISPATCHED: "processing",
  DELIVERED: "success",
  CANCELLED: "error",
  FAILED: "default",
  PACKED: "cyan",
  PACKING: "warning",
  PICKING: "geekblue",
  CNR_IN_PROGRESS: "orange",
};

const ORDER_COLOR_MAP: Record<string, string> = {
  GREEN: "#52c41a",
  YELLOW: "#faad14",
  RED: "#f5222d",
  BLUE: "#1890ff",
  ORANGE: "#fa8c16",
};

const formatAddress = (order: ITrayOrder): string => {
  const addr = order.metadata?.address;
  if (!addr) return "-";
  return [
    addr.house && addr.floor ? `${addr.house}, Floor ${addr.floor}` : addr.house,
    addr.tower,
    addr.society,
    addr.city,
    addr.state,
    addr.pincode,
  ]
    .filter(Boolean)
    .join(", ");
};

const MarkHoldOrdersPage = () => {
  useDocumentTitle("Mark Hold Orders");
  const dispatch = useAppDispatch();

  const { darkStores, cities } = useAppSelector((state) => state.adminServices);
  const {
    trayOrder,
    trayStatus,
    trayError,
    dispositions,
    dispositionsStatus,
    markFailedStatus,
    markFailedError,
  } = useAppSelector((state) => state.markHoldOrders);

  const [cityId, setCityId] = useState<string | undefined>();
  const [storeId, setStoreId] = useState<string | undefined>();
  const [trayCode, setTrayCode] = useState("");
  const [selectedReason, setSelectedReason] = useState<string | undefined>();

  useEffect(() => {
    dispatch(getDarkStores());
    dispatch(getCities());
    dispatch(getDeliveryFailureDispositions());
    return () => {
      dispatch(resetTrayOrder());
    };
  }, [dispatch]);

  useEffect(() => {
    if (markFailedStatus === "success") {
      message.success({ content: "Order marked as failed successfully", key: MESSAGE_KEY });
      setSelectedReason(undefined);
      setTrayCode("");
      dispatch(resetTrayOrder());
      dispatch(resetMarkFailedStatus());
    } else if (markFailedStatus === "error") {
      message.error({
        content: markFailedError || "Failed to mark order as failed",
        key: MESSAGE_KEY,
      });
      dispatch(resetMarkFailedStatus());
    }
  }, [markFailedStatus, markFailedError, dispatch]);

  const handleCityChange = (value: string) => {
    setCityId(value);
    setStoreId(undefined);
    dispatch(resetTrayOrder());
    setTrayCode("");
    setSelectedReason(undefined);
  };

  const handleStoreChange = (value: string) => {
    setStoreId(value);
    dispatch(resetTrayOrder());
    setTrayCode("");
    setSelectedReason(undefined);
  };

  const handleSearch = useCallback(() => {
    const trimmed = trayCode.trim();
    if (!trimmed || !storeId) return;
    setSelectedReason(undefined);
    dispatch(getOrderByTrayCode({ trayCode: trimmed, storeId }));
  }, [dispatch, trayCode, storeId]);

  const handleTrayCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTrayCode(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleMarkFailed = () => {
    if (!trayOrder || !selectedReason) return;
    message.loading({ content: "Marking order as failed...", key: MESSAGE_KEY });
    dispatch(markOrderFailed({ orderId: trayOrder.id, remarks: selectedReason }));
  };

  const canMarkFailed =
    !!trayOrder &&
    (trayOrder.status === "DISPATCHED" || trayOrder.status === "CNR_IN_PROGRESS");

  const isMarkFailedEnabled =
    !!selectedReason &&
    canMarkFailed &&
    markFailedStatus !== "saving";

  const filteredStores = (darkStores ?? []).filter(
    (s) => s.cityId?.toString() === cityId
  );

  return (
    <PageWrapper>
      <AppPageHeader title="Mark Hold Orders" />

      <Card>
        <Form layout="vertical">
          <Row gutter={24}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="City" style={{ marginBottom: 16 }}>
                <Select
                  showSearch
                  placeholder="Select city"
                  value={cityId}
                  onChange={handleCityChange}
                  filterOption={(input, option) =>
                    option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
                  }
                >
                  {(cities ?? []).map((city) => (
                    <Select.Option value={city.id?.toString()} key={city.id}>
                      {city.name} || {city.id}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {cityId && (
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="Store" style={{ marginBottom: 16 }}>
                  <Select
                    showSearch
                    placeholder="Select store"
                    value={storeId}
                    onChange={handleStoreChange}
                    filterOption={(input, option) =>
                      option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
                    }
                  >
                    {filteredStores.map((store) => (
                      <Select.Option value={store.id?.toString()} key={store.id}>
                        {store.name} || {store.id}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>

          {storeId && (
            <SearchRow>
              <Input
                placeholder="Enter Tray ID"
                value={trayCode}
                onChange={handleTrayCodeChange}
                onKeyDown={handleKeyDown}
                style={{ maxWidth: 280 }}
                disabled={trayStatus === "loading"}
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={trayStatus === "loading"}
                disabled={!trayCode.trim()}
              >
                Search
              </Button>
            </SearchRow>
          )}
        </Form>
      </Card>

      {trayStatus === "loading" && (
        <div style={{ textAlign: "center", padding: "48px 0" }}>
          <Spin size="large" tip="Fetching order..." />
        </div>
      )}

      {trayStatus === "success" && trayOrder === null && (
        <EmptyState>No order found for tray-id {trayCode}</EmptyState>
      )}

      {trayStatus === "error" && (
        <EmptyState style={{ color: "#f5222d", borderColor: "#ffccc7", background: "#fff2f0" }}>
          {trayError || "Failed to fetch order"}
        </EmptyState>
      )}

      {trayStatus === "success" && trayOrder && (
        <>
          <Card
            title={
              <Row align="middle" gutter={[8, 4]} wrap>
                <Col>
                  <Text strong style={{ fontSize: 15 }}>
                    {trayOrder.metadata?.displayOrderId}
                  </Text>
                </Col>
                <Col>
                  <Tag color={STATUS_COLOR_MAP[trayOrder.status] || "default"}>
                    {trayOrder.status}
                  </Tag>
                </Col>
                {trayOrder.metadata?.isVip && (
                  <Col>
                    <Tag color="gold">VIP</Tag>
                  </Col>
                )}
                {trayOrder.color && (
                  <Col>
                    <Tag
                      style={{
                        backgroundColor: ORDER_COLOR_MAP[trayOrder.color] || undefined,
                        color: "#fff",
                        borderColor: "transparent",
                      }}
                    >
                      {trayOrder.color}
                    </Tag>
                  </Col>
                )}
              </Row>
            }
          >
            <Row gutter={[24, 16]}>
              <Col xs={12} sm={8}>
                <InfoLabel>Delivery Date</InfoLabel>
                <InfoValue>{trayOrder.deliveryDate}</InfoValue>
              </Col>
              <Col xs={12} sm={8}>
                <InfoLabel>Slot</InfoLabel>
                <InfoValue>{trayOrder.slot}</InfoValue>
              </Col>
              <Col xs={12} sm={8}>
                <InfoLabel>Tray IDs</InfoLabel>
                <div>
                  {normalizeTrayCodes(trayOrder.metadata).map((id) => (
                    <Tag key={id} style={{ marginBottom: 2 }}>
                      {id}
                    </Tag>
                  ))}
                </div>
              </Col>
              <Col xs={12} sm={8}>
                <InfoLabel>Batch / Sequence</InfoLabel>
                <InfoValue>
                  Batch {trayOrder.batchId} · Seq {trayOrder.sequenceNo}
                </InfoValue>
              </Col>
              <Col xs={12} sm={8}>
                <InfoLabel>Store ID</InfoLabel>
                <InfoValue>{trayOrder.storeId}</InfoValue>
              </Col>
              <Col xs={12} sm={8}>
                <InfoLabel>Customer</InfoLabel>
                <InfoValue>{trayOrder.metadata?.contactDetails?.name || "-"}</InfoValue>
              </Col>
              <Col xs={24}>
                <InfoLabel>Delivery Address</InfoLabel>
                <InfoValue style={{ fontWeight: 400, color: "#262626" }}>
                  {formatAddress(trayOrder)}
                </InfoValue>
              </Col>
            </Row>

            <Divider orientation="left" style={{ marginTop: 20, marginBottom: 12, fontSize: 13 }}>
              Order Items ({trayOrder.orderItems?.length || 0})
            </Divider>

            {trayOrder.orderItems?.map((item) => (
              <ItemRow key={item.orderItemId}>
                <Image
                  src={item.productImage}
                  width={60}
                  height={60}
                  style={{ objectFit: "cover", borderRadius: 6, minWidth: 60 }}
                  preview={false}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                />
                <ItemInfo>
                  <Text strong style={{ display: "block", fontSize: 14 }}>
                    {item.productName}
                  </Text>
                  {item.packetDescription && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {item.packetDescription}
                    </Text>
                  )}
                  <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 2 }}>
                    SKU: {item.skuCode}
                  </Text>
                </ItemInfo>
                <div style={{ textAlign: "right", minWidth: 90 }}>
                  <Text strong style={{ display: "block", fontSize: 14 }}>
                    {item.finalQty} {item.suffix}
                  </Text>
                  {item.color && (
                    <Tag
                      style={{
                        marginTop: 4,
                        fontSize: 11,
                        backgroundColor: ORDER_COLOR_MAP[item.color] || undefined,
                        color: "#fff",
                        borderColor: "transparent",
                      }}
                    >
                      {item.color}
                    </Tag>
                  )}
                </div>
              </ItemRow>
            ))}
          </Card>

          <Card title="Mark Order as Failed">
            <Row gutter={[16, 12]} align="bottom">
              <Col xs={24} sm={14} md={12}>
                <InfoLabel>Select Reason *</InfoLabel>
                <Select
                  placeholder="Select a reason"
                  value={selectedReason}
                  onChange={setSelectedReason}
                  style={{ width: "100%" }}
                  loading={dispositionsStatus === "loading"}
                  disabled={dispositionsStatus === "loading"}
                  size="large"
                >
                  {dispositions.map((reason) => (
                    <Select.Option key={reason} value={reason}>
                      {reason}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={10} md={8}>
                {!canMarkFailed && (
                  <div style={{ marginBottom: 6 }}>
                    <Text type="warning" style={{ fontSize: 12, display: "block" }}>
                      Order status must be DISPATCHED or CNR_IN_PROGRESS to mark as failed (current:{" "}
                      {trayOrder.status})
                    </Text>
                  </div>
                )}
                <Button
                  type="primary"
                  danger
                  size="large"
                  onClick={handleMarkFailed}
                  disabled={!isMarkFailedEnabled}
                  loading={markFailedStatus === "saving"}
                  block
                >
                  Mark Failed
                </Button>
              </Col>
            </Row>
          </Card>
        </>
      )}
    </PageWrapper>
  );
};

export default MarkHoldOrdersPage;
